import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';

const TITLE_MAP = {
  error: 'Error',
  success: 'Success',
  warning: 'Warning',
};

/**
 * Generic MessageBox Component
 */
class MessageBox extends React.PureComponent {
  static defaultProps = {
    type: 'warning',
    hide: false,
    removeable: false,
    checkmark: false,
    noTitle: false,
    className: '',
  };

  static propTypes = {
    /**
     * Optional ID passed to the top most parent DOM element
     */
    id: PropTypes.string,

    /**
     * Type of message box, default is warning
     */
    type: PropTypes.oneOf(['warning', 'success', 'error', 'info']),
    /**
     * Message(s) to show. If you pass in an array of strings, multiple messages
     * will be displayed. You can also pass in a customized React component
     */
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.node]),
    /**
     * Since 'text' prop isn't always a string we can't rely on it.
     * Message will passed to Mixpanel will be, in order of preference:
     * 1. instrumentationText (if passed in)
     * 2. text (only if it's a string)
     * 3. title
     */
    instrumentationText: PropTypes.string,
    /**
     * Title to be displayed, default is warning
     */
    title: PropTypes.string,
    /**
     * Whether the title should be displayed or not, default is false
     */
    noTitle: PropTypes.bool,
    /**
     * Hide component or not
     */
    hide: PropTypes.bool,
    /**
     * Adds a close button to the message box
     */
    removeable: PropTypes.bool,
    /**
     * Adds a checkmark to the message box before the text
     */
    checkmark: PropTypes.bool,
    /**
     * Optional className for outermost div
     */
    className: PropTypes.string,
    /**
     * Optional callback that is invoked when close button is clicked
     */
    onClose: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      userClickedClose: false,
    };
  }

  componentDidMount() {
    const { text, hide } = this.props;
    const isReactComponent = React.isValidElement(text);

    if (!hide && (isReactComponent || (text && text.length))) {
      this.fireMixpanelEvent();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.hide && this.props.hide) {
      this.fireMixpanelEvent();
    }
  }

  fireMixpanelEvent() {
    const { text, title } = this.props;
    let message = this.props.instrumentationText;

    if (!message) {
      message = typeof text === 'string' ? text : title;
    }
    // Deprecated, will be removed in the future!
    this.context?.i13n?.executeEvent('log', {
      label: `User ${this.props.type} notification shown`,
      params: {
        title,
        message,
        component: 'MessageBox',
      },
    });
  }

  /**
   * Method to render title node
   * @params {String} title text to show on title
   * @params {String} type message box type
   * @return {Object} title node
   */
  renderTitle = (title, type, noTitle) => {
    if (noTitle) {
      return null;
    }
    return (
      <div className="message-box__title" data-testid="title">
        {title || TITLE_MAP[type] || ''}
      </div>
    );
  };
  /**
   * Method to render message(s)
   * @params {Array} text messages
   * @return {Object} message node
   */
  renderMessage = (text) => {
    return text.map((sentence, i) => {
      return (
        <div className="message-box__message" data-testid={`message-${i}`} key={i}>
          {sentence}
        </div>
      );
    });
  };

  /**
   * If the checkmark prop is true render a success checkmark
   */
  renderCheckmark = () => {
    return this.props.checkmark ? <div className="message-box__checkmark" /> : null;
  };

  /**
   * If the removeable prop is true, render a close button
   */
  renderCloseBtn = () => {
    return this.props.removeable ? (
      <span
        className="message-box__close"
        onClick={() => {
          this.setState({ userClickedClose: true });
          this.props.onClose && this.props.onClose();
        }}
      >
        ×
      </span>
    ) : null;
  };

  render() {
    const { title, type, noTitle, hide } = this.props;
    let { text } = this.props;

    if (!React.isValidElement(text) && (!text || !text.length)) {
      return null;
    }

    const hideBox = hide || this.state.userClickedClose;

    // always convert to array
    text = [].concat(text);
    return (
      <div
        id={this.props.id}
        className={classNames(`message-box message-box--${type}`, {
          'message-box--hide': hideBox,
          [this.props.className]: !!this.props.className,
        })}
        data-testid="message-box"
        data-type={`${type}`}
      >
        {this.renderCheckmark()}
        <div className="message-box__content">
          {this.renderTitle(title, type, noTitle)}
          {this.renderMessage(text)}
        </div>
        {this.renderCloseBtn()}
      </div>
    );
  }
}

MessageBox.contextTypes = {
  i13n: PropTypes.object,
};

export default MessageBox;
